import { Choice, Module, SelectComponent as SelectComponentI, SelectOptionComponent, Step, STEP_TYPE } from '@abiglobalhealth/go-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { UserFormData } from '@/contexts/userForm'
import { useChoices } from '@/hooks/useChoices'
import { useFormErrors } from '@/hooks/useFormErrors'
import { useModule } from '@/hooks/useModule'
import { useStep } from '@/hooks/useStep'
import { useUserForm } from '@/hooks/useUserForm'

import { Select } from '../Select'

const getInitialValue = (
  componentId: string,
  choices: Choice[],
  options: SelectOptionComponent[],
  currentStep?: Step,
  currentModule?: Module,
  userValue?: UserFormData
) => {
  if (!currentStep || !currentModule) return null

  const choice = choices.find(
    (choice) => choice.stepId === currentStep.id && choice.moduleId === currentModule.id && choice.component.id === componentId
  )

  if (choice) {
    return options.find((option) => option.id === choice.optionId) || null
  }

  if (userValue) {
    return options.find((option) => option.value === userValue) || null
  }
  return null
}

export type SelectComponentProps = Omit<SelectComponentI, 'componentType'> & {
  tabIndex?: number
}

export const SelectComponent = ({ id, textKey, options, required, placeholder, tabIndex }: SelectComponentProps) => {
  const { t } = useTranslation()
  const { addChoice, choices } = useChoices()
  const { addData, getUserDataById } = useUserForm()
  const { currentStep } = useStep()
  const { currentModule } = useModule()
  const { clearFormError, getFieldError } = useFormErrors()
  const [selected, setSelected] = useState<SelectOptionComponent | null>(() =>
    getInitialValue(id, choices, options, currentStep, currentModule, getUserDataById(id))
  )

  useEffect(() => {
    if (!selected) return

    switch (currentStep?.type) {
      case STEP_TYPE.USER_FORM:
        addData(id, selected.value)
        break

      case STEP_TYPE.CHOICES: {
        if (!currentModule?.id) throw new Error('Module id is undefined')

        if (!selected) throw new Error('No selected option found')

        addChoice({
          date: new Date(),
          component: {
            id,
            key: textKey,
            text: t(textKey)
          },
          stepId: currentStep.id,
          moduleId: currentModule.id,
          options: [
            {
              optionId: selected.id,
              value: {
                key: selected.value,
                text: t(selected.textKey)
              }
            }
          ]
        })
        break
      }
    }
  }, [selected])

  const error = getFieldError(id)

  return (
    <Select
      id={id}
      tabIndex={tabIndex}
      textKey={textKey}
      options={options}
      required={required}
      placeholder={placeholder}
      onChange={(option) => {
        clearFormError(id)
        setSelected(option)
      }}
      value={selected?.value}
      error={error}
    />
  )
}
